// Layer 1: Resource Position Layer
// Handles all resource coordinate logic - both legacy (matrix) and new (API coordinates)

import { ICoordinate, Matrix } from '../../types';
import { locateResourceInMatrix } from '../matrix';

export interface IResourceData {
  id: number;
  resourceTypeId?: number;
}

export interface IResourcePosition {
  id: number;
  x: number;
  y: number;
  isGridPosition: boolean; // true for matrix coords, false for pixel coords
}

export interface IApiCoordinateMarker {
  resource: { id: number };
  coordinate: ICoordinate;
}

/**
 * Layer 1: Resource Position Layer
 *
 * Determines the base coordinates for each resource.
 * Supports both legacy matrix-based and new API coordinate systems.
 */
export class ResourcePositionLayer {
  /**
   * Gets positions using legacy matrix system
   * Returns grid coordinates that need to be converted to pixels
   */
  static getPositionsFromMatrix(resources: IResourceData[], matrix?: Matrix): IResourcePosition[] {
    if (!matrix) {
      return [];
    }

    return resources
      .map((resource) => {
        const coords = locateResourceInMatrix(resource.id, matrix);
        if (!coords) {
          return null;
        }

        return {
          id: resource.id,
          x: coords.x,
          y: coords.y,
          isGridPosition: true,
        };
      })
      .filter((pos): pos is IResourcePosition => pos !== null);
  }

  /**
   * Gets positions using new API coordinate system
   * Returns pixel coordinates ready to use
   */
  static getPositionsFromAPI(resources: IResourceData[], markers?: IApiCoordinateMarker[]): IResourcePosition[] {
    if (!markers?.length) {
      return [];
    }

    return resources
      .map((resource) => {
        const marker = markers.find((m) => m.resource.id === resource.id);
        if (!marker?.coordinate) {
          return null;
        }

        return {
          id: resource.id,
          x: marker.coordinate.x,
          y: marker.coordinate.y,
          isGridPosition: false,
        };
      })
      .filter((pos): pos is IResourcePosition => pos !== null);
  }

  /**
   * Auto-detects which system to use and returns positions
   * Prefers new API coordinates if available, falls back to matrix
   */
  static getPositions(
    resources: IResourceData[],
    matrix?: Matrix,
    markers?: IApiCoordinateMarker[]
  ): IResourcePosition[] {
    // Prefer new API coordinate system
    if (markers?.length) {
      return this.getPositionsFromAPI(resources, markers);
    }

    // Fallback to legacy matrix system
    return this.getPositionsFromMatrix(resources, matrix);
  }
}
